import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { rxResource } from '@angular/core/rxjs-interop';
import { MatIconModule } from '@angular/material/icon';
import { RouterLink } from '@angular/router';
import { APP_PATHS, XEMEX_OID } from '@core/constants';
import { Installation, getUserFeaturePermissions } from '@core/models';
import { LocationInstallationGroup } from '@core/models/location.model';
import { InstallationGroupService } from '@core/services/installation-group.service';
import { LocationService } from '@core/services/location.service';
import { CardComponent } from '@custom-components/general/card';
import { DetailFieldComponent } from '@custom-components/general/detail-field/detail-field.component';
import { TranslateModule } from '@ngx-translate/core';
import { AuthStore } from '@root/app/store/auth.store';
import { UserStore } from '@root/app/store/user.store';
import { Observable, catchError, forkJoin, map, of } from 'rxjs';
import { FedDevice } from '../../fed-devices';
import { mergeInstallationGroups } from '../../installation-groups';

const DEVICE_GROUPS_LIMIT = 100;

interface GroupsRead {
  groups: LocationInstallationGroup[];
  partial: boolean;
}

interface GroupsSource {
  groups: LocationInstallationGroup[];
  failed: boolean;
}

interface GroupsParams {
  locationId: number;
  deviceIds: number[];
}

@Component({
  selector: 'xemex-installation-ownership-card',
  imports: [CardComponent, DetailFieldComponent, MatIconModule, RouterLink, TranslateModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './installation-ownership-card.component.html',
  styleUrl: './installation-ownership-card.component.scss',
})
export class InstallationOwnershipCardComponent {
  installation = input.required<Installation>();
  devices = input.required<FedDevice[]>();

  private readonly locationService = inject(LocationService);
  private readonly groupService = inject(InstallationGroupService);
  private readonly userStore = inject(UserStore);
  private readonly authStore = inject(AuthStore);

  protected readonly APP_PATHS = APP_PATHS;

  protected readonly canViewGroups = computed(() =>
    getUserFeaturePermissions(
      this.userStore.user(),
      this.authStore.permissions(),
      XEMEX_OID,
    ).includes('view_installation_groups'),
  );

  // A group reaches an installation through its location or through one of its devices, and no
  // single route answers both, so both are read. The read follows the ids rather than the
  // installation, so reading the installation again does not read the groups again.
  private readonly groupsParams = computed<GroupsParams | undefined>(
    () =>
      this.canViewGroups()
        ? {
            locationId: this.installation().location.locationId,
            deviceIds: this.devices().map(device => device.deviceId),
          }
        : undefined,
    { equal: (a, b) => JSON.stringify(a) === JSON.stringify(b) },
  );

  private readonly groupsRead = rxResource({
    params: this.groupsParams,
    stream: ({ params }) => this.read(params),
  });

  protected readonly groups = computed(() => this.groupsRead.value()?.groups ?? []);
  protected readonly groupsLoading = computed(
    () => !this.groupsRead.hasValue() && this.groupsRead.isLoading(),
  );
  protected readonly groupsPartial = computed(() => this.groupsRead.value()?.partial ?? false);

  private read({ locationId, deviceIds }: GroupsParams): Observable<GroupsRead> {
    const sources: Observable<LocationInstallationGroup[]>[] = [
      this.locationService
        .getLocationDetail(String(locationId))
        .pipe(map(detail => (detail?.installationGroups ?? []) as LocationInstallationGroup[])),
      ...deviceIds.map(deviceId =>
        this.groupService
          .getInstallationGroups({ deviceId, limit: DEVICE_GROUPS_LIMIT, offset: 0 })
          .pipe(map(response => response.installationGroups)),
      ),
    ];
    return forkJoin(
      sources.map(source =>
        source.pipe(
          map((groups): GroupsSource => ({ groups, failed: false })),
          catchError(() => of<GroupsSource>({ groups: [], failed: true })),
        ),
      ),
    ).pipe(
      map(results => ({
        groups: mergeInstallationGroups(results.map(result => result.groups)),
        partial: results.some(result => result.failed),
      })),
    );
  }
}
